<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { resources } from '@/config/brands'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

// 定义样式变量供 CSS v-bind 使用
const avatarBorderColor = props.config.theme.avatarBorder
const vipSubscribeBgUrl = props.config.theme.vipSubscribeBg
const subscribeText = props.config.theme?.subscribeText
const subscribeText2 = props.config.theme?.subscribeText2
const subscribeBg = props.config.theme?.subscribeBg
</script>

<template>
  <div class="cover">
    <!-- 头像 -->
    <div class="px-30px pt-40px pb-27px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-15 h-15 border rounded-full avatar-border" :src="userStore.$state.thirdHeadImgUrl" alt="avatar">
        <img v-else class="w-15 h-15 rounded-full" :src="config.resources.userDefaultAvatar" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="text-18px overflow-hidden whitespace-nowrap text-ellipsis text-n-text-inverse">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
          <div v-if="userStore.$state.subscribe" class="shrink-0">
            <img src="../icons/ficmoon/vip.png" class="w-18px h-14px ml-8px">
          </div>
        </div>
        <p class="text-11px text-n-text-3">
          UID {{ userStore.$state.code }}
        </p>
      </div>

      <div v-if="!userStore.$state.thirdPartType && config.showLoginBtn !== false" type="link" class="shrink-0">
        <a class="px-14px py-7px text-n-text-1 text-13px font-bold rounded-full border border-n-text-inverse" @click="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>

    <!-- vip 订阅 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover b-rd-2 flex items-center justify-between mx-10px px-4">
      <div>
        <div class="text-18px font-medium subscribe-text">
          <span v-if="!userStore.$state.subscribe" class="line-clamp-1">{{ config.texts.getVip }}</span>
          <span v-else>{{ config.texts.vipMembership }}</span>
        </div>
        <div v-if="!userStore.$state.subscribe" class="subscribe-text-2">
          {{ config.texts.moreCoinsMoreBenefits }}
        </div>
        <div v-if="userStore.$state.subscribeEndDate" class="subscribe-text-2">
          {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
        </div>
      </div>
      <router-link
        v-if="!userStore.$state.subscribe"
        :to="{ name: 'subscribe' }"
      >
        <div class="b-rd-16px subscribe-bg text-center w-80px py-1 font-medium text-n-text-inverse" @click="elementClick('Details')">
          {{ config.texts.details }}
        </div>
      </router-link>
    </div>

    <!-- 充值,金币信息 -->
    <div class="b-rd-2 mx-10px mt-3 bg-n-bg-card p-4 flex justify-between items-center h-20">
      <div>
        <strong class="text-n-text-1 text-base">{{ config.texts.myWallet }}</strong>
        <div class="flex items-center mt-4px space-x-8px">
          <img :src="resources?.coin" width="20px" height="20px" alt="金币图标">
          <strong class="text-n-text-1">{{ userStore.$state.coinBalance }}</strong>
          <span class="text-12px text-n-text-2">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div v-if="!loading">
        <router-link :to="topupShow ? { name: 'pay' } : { name: 'welfare' }">
          <div
            class="ml-10px px-14px py-7px text-13px font-bold rounded-full btn-solid text-n-text-inverse"
            @click="elementClick('TopUp')"
          >
            {{ topupShow ? config.texts.topUp : `&nbsp;&nbsp;&nbsp; ${config.texts.get} &nbsp;&nbsp;&nbsp;` }}
          </div>
        </router-link>
      </div>
    </div>

    <!-- setting -->
    <div class="mt-10px mx-10px px-20px b-rd-2 bg-n-bg-card">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <img src="../icons/ficmoon/transaction.png" width="20px" height="20px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <img src="../icons/ficmoon/unlocked.png" width="20px" height="20px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <img src="../icons/ficmoon/contact-us.png" width="20px" height="20px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <img src="../icons/ficmoon/setting.png" width="20px" height="20px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.cover {
  width: 100%;
}

.subscribe-text {
  color: v-bind(subscribeText);
}
.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-bg {
  background: v-bind(subscribeBg) no-repeat top left;
  background-size: 100% 100%;
}
.avatar-border {
  border-color: v-bind(avatarBorderColor);
}

.vip-cover {
  height: 80px;
  background: v-bind(vipSubscribeBgUrl) no-repeat top left;
  background-size: 100% 100%;
  border-radius: 8px;
}
</style>
